import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

import { BugoStatResult } from './adminBugoStat.types';
import { rateOf } from './adminBugoStat.util';

//* 프로젝트 표준(커뮤니티 xlsx + file-saver). 스타일 플러그인은 쓰지 않고
//* 너비/고정/필터/숫자포맷으로만 가독성을 낸다.

const charWidth = (value: unknown): number => {
  const text = value === null || value === undefined ? '' : String(value);
  //* 한글은 영문보다 넓다 — 대략 2배로 잡는다
  // eslint-disable-next-line no-control-regex
  return text.split('').reduce((sum, ch) => sum + (/[^\x00-\xff]/.test(ch) ? 2 : 1), 0);
};

const autoWidth = (rows: (string | number | null)[][]): XLSX.ColInfo[] => {
  const widths: number[] = [];
  rows.forEach((row) => {
    row.forEach((cell, index) => {
      widths[index] = Math.max(widths[index] ?? 0, charWidth(cell));
    });
  });
  return widths.map((w) => ({ wch: Math.min(Math.max(w + 2, 6), 40) }));
};

const HEADER = [
  '상조',
  '상가수',
  '공유 상가',
  '미공유 상가',
  '공유율(%)',
  '총 공유횟수',
  '상가당 평균 공유',
  '조의글 설정 상가',
  '조의글 설정률(%)',
  '조의글 수',
  '조의글 달린 상가',
  '글달린상가당 조의글',
  '부고(상주)수',
  '발송',
  '미발송',
  '발송률(%)',
  '미발송 상가',
];

const round1 = (value: number | null): number | null =>
  value === null ? null : Math.round(value * 10) / 10;

/** 평균 — 분모 0이면 빈 칸. 공유는 전체 상가, 조의글은 '글 달린 상가'가 분모다 */
const avgOf = (total: number, denominator: number): number | null =>
  denominator > 0 ? Math.round((total / denominator) * 10) / 10 : null;

/** 헤더 고정 — 커뮤니티 xlsx는 !views까지 있어야 실제로 고정된다 */
const freezeHeader = (ws: XLSX.WorkSheet): void => {
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };
  ws['!views'] = [{ state: 'frozen', ySplit: 1 }] as XLSX.WorkSheet['!views'];
};

//* 집계할 수 없는 수치는 0이 아니라 '-'로 — 파일만 받아 본 사람이 "발송 0건"으로 읽으면 안 된다
const UNAVAILABLE = '-';

export const exportBugoStatExcel = (
  result: BugoStatResult,
  period: { from: string; to: string },
): void => {
  const { bugoDataUnavailable, acqMsgDataUnavailable } = result;
  const bugoCell = (value: number | null) =>
    bugoDataUnavailable ? UNAVAILABLE : value;
  const acqMsgCell = (value: number | null) =>
    acqMsgDataUnavailable ? UNAVAILABLE : value;

  const body = result.brands.map((row) => [
    row.brandName,
    row.feventCount,
    row.sharedFeventCount,
    row.unsharedFeventCount,
    round1(rateOf(row.sharedFeventCount, row.feventCount)),
    row.shareCountTotal,
    avgOf(row.shareCountTotal, row.feventCount),
    row.acqMsgFeventCount,
    round1(rateOf(row.acqMsgFeventCount, row.feventCount)),
    acqMsgCell(row.acqMsgTotal),
    acqMsgCell(row.acqMsgWrittenFeventCount),
    acqMsgCell(avgOf(row.acqMsgTotal, row.acqMsgWrittenFeventCount)),
    bugoCell(row.bugoCount),
    bugoCell(row.sentBugoCount),
    bugoCell(row.unsentBugoCount),
    bugoCell(round1(rateOf(row.sentBugoCount, row.bugoCount))),
    bugoCell(row.unsentFeventCount),
  ]);

  const totals = result.totals;
  const totalRow = [
    '합계',
    totals.feventCount,
    totals.sharedFeventCount,
    totals.unsharedFeventCount,
    round1(rateOf(totals.sharedFeventCount, totals.feventCount)),
    totals.shareCountTotal,
    avgOf(totals.shareCountTotal, totals.feventCount),
    totals.acqMsgFeventCount,
    round1(rateOf(totals.acqMsgFeventCount, totals.feventCount)),
    acqMsgCell(totals.acqMsgTotal),
    acqMsgCell(totals.acqMsgWrittenFeventCount),
    acqMsgCell(avgOf(totals.acqMsgTotal, totals.acqMsgWrittenFeventCount)),
    bugoCell(totals.bugoCount),
    bugoCell(totals.sentBugoCount),
    bugoCell(totals.unsentBugoCount),
    bugoCell(round1(rateOf(totals.sentBugoCount, totals.bugoCount))),
    bugoCell(totals.unsentFeventCount),
  ];

  //* 화면 경고 배너와 같은 내용을 파일에도 남긴다 (표만 떼어 봐도 알 수 있게)
  const noteRows: (string | number | null)[][] = [];
  if (bugoDataUnavailable) {
    noteRows.push(['※ 이번 조회에서 부고 발송 수치를 집계하지 못해 -로 표기했습니다']);
  }
  if (acqMsgDataUnavailable) {
    noteRows.push(['※ 이번 조회에서 조의글 수를 집계하지 못해 -로 표기했습니다']);
  }

  const rows: (string | number | null)[][] = [HEADER, ...body, totalRow, ...noteRows];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = autoWidth(rows);
  //* 헤더 고정 + 자동 필터 (합계·안내행은 필터 범위에서 제외 — 헤더 1줄 + 본문 N줄까지만)
  freezeHeader(ws);
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: body.length, c: HEADER.length - 1 },
    }),
  };
  XLSX.utils.book_append_sheet(wb, ws, '상조별');

  //* 발인일별 추이는 별도 시트로
  const dayRows: (string | number | null)[][] = [
    ['발인일', '상가수', '공유 상가', '공유율(%)'],
    ...result.days.map((day) => [
      day.date,
      day.feventCount,
      day.sharedFeventCount,
      round1(rateOf(day.sharedFeventCount, day.feventCount)),
    ]),
  ];
  const dayWs = XLSX.utils.aoa_to_sheet(dayRows);
  dayWs['!cols'] = autoWidth(dayRows);
  freezeHeader(dayWs);
  XLSX.utils.book_append_sheet(wb, dayWs, '발인일별');

  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([out], { type: 'application/octet-stream' }),
    `부고통계_${period.from}_${period.to}.xlsx`,
  );
};
